import { createSlice } from "@reduxjs/toolkit";
import {
  createCertificateTemplate,
  getActiveCertificateTemplates,
  getAllCertificateTemplates,
  getCertificateTemplateById,
  getCertificateTemplatesByType,
  getCertificateTemplatesByTypeAndEntity,
  updateCertificateTemplate,
} from "./certificateTemplateService";

/* -------------------- Status Enum -------------------- */

export const STATUS = {
  IDLE: "idle",
  LOADING: "loading",
  SUCCEEDED: "succeeded",
  FAILED: "failed",
};

/* -------------------- Initial State -------------------- */

const initialState = {
  createCertificateTemplate: {
    status: STATUS.IDLE,
    error: null,
    message: null,
  },

  certificateTemplateList: {
    status: STATUS.IDLE,
    error: null,
    data: [],
    pageCount: 0,
  },

  updateCertificateTemplate: {
    status: STATUS.IDLE,
    error: null,
    message: null,
  },

  activeCertificateTemplatesList: {
    status: STATUS.IDLE,
    error: null,
    data: [],
  },

  certificateTemplateDetails: {
    status: STATUS.IDLE,
    error: null,
    data: [],
  },

  certificateTemplateByType: {
    status: STATUS.IDLE,
    error: null,
    data: [],
  },

  certificateTemplateByTypeAndEntity: {
    status: STATUS.IDLE,
    error: null,
    data: [],
  },
};

/* -------------------- Slice -------------------- */

const certificateTemplateSlice = createSlice({
  name: "certificateTemplate",
  initialState,
  reducers: {
  resetCertificateTemplateDetails: (state) => {
    state.certificateTemplateDetails.status = STATUS.IDLE;
    state.certificateTemplateDetails.error = null;
    state.certificateTemplateDetails.data = [];
  },
  },
  extraReducers: (builder) => {
    /* ---------- Create Certificate Template ---------- */
    builder
      .addCase(createCertificateTemplate.pending, (state) => {
        state.createCertificateTemplate.status = STATUS.LOADING;
        state.createCertificateTemplate.error = null;
      })
      .addCase(createCertificateTemplate.fulfilled, (state, action) => {
        state.createCertificateTemplate.status = STATUS.SUCCEEDED;
        state.createCertificateTemplate.message = action?.payload;
      })
      .addCase(createCertificateTemplate.rejected, (state, action) => {
        state.createCertificateTemplate.status = STATUS.FAILED;
        state.createCertificateTemplate.error = action?.payload;
      });

    /* ---------- Get Certificate Template List ---------- */
    builder
      .addCase(getAllCertificateTemplates.pending, (state) => {
        state.certificateTemplateList.status = STATUS.LOADING;
        state.certificateTemplateList.error = null;
      })
      .addCase(getAllCertificateTemplates.fulfilled, (state, action) => {
        state.certificateTemplateList.status = STATUS.SUCCEEDED;
        state.certificateTemplateList.data = action?.payload || [];
        state.certificateTemplateList.pageCount = action.payload?.pageCount || 0;
      })
      .addCase(getAllCertificateTemplates.rejected, (state, action) => {
        state.certificateTemplateList.status = STATUS.FAILED;
        state.certificateTemplateList.error = action.payload?.message;
      });

    /* ---------- Update Certificate Template ---------- */
    builder
      .addCase(updateCertificateTemplate.pending, (state) => {
        state.updateCertificateTemplate.status = STATUS.LOADING;
        state.updateCertificateTemplate.error = null;
      })
      .addCase(updateCertificateTemplate.fulfilled, (state, action) => {
        state.updateCertificateTemplate.status = STATUS.SUCCEEDED;
        state.updateCertificateTemplate.message = action?.payload;
      })
      .addCase(updateCertificateTemplate.rejected, (state, action) => {
        state.updateCertificateTemplate.status = STATUS.FAILED;
        state.updateCertificateTemplate.error = action?.payload;
      });

    /* ---------- Get Active Certificate Entity List ---------- */
    builder
      .addCase(getActiveCertificateTemplates.pending, (state) => {
        state.activeCertificateTemplatesList.status = STATUS.LOADING;
        state.activeCertificateTemplatesList.error = null;
      })
      .addCase(getActiveCertificateTemplates.fulfilled, (state, action) => {
        state.activeCertificateTemplatesList.status = STATUS.SUCCEEDED;
        state.activeCertificateTemplatesList.data = action?.payload || [];
      })
      .addCase(getActiveCertificateTemplates.rejected, (state, action) => {
        state.activeCertificateTemplatesList.status = STATUS.FAILED;
        state.activeCertificateTemplatesList.error = action.payload?.message;
      });

    /* ---------- Get Certificate Template by Id ---------- */
    builder
      .addCase(getCertificateTemplateById.pending, (state) => {
        state.certificateTemplateDetails.status = STATUS.LOADING;
        state.certificateTemplateDetails.error = null;
      })
      .addCase(getCertificateTemplateById.fulfilled, (state, action) => {
        state.certificateTemplateDetails.status = STATUS.SUCCEEDED;
        state.certificateTemplateDetails.data = action?.payload || [];
      })
      .addCase(getCertificateTemplateById.rejected, (state, action) => {
        state.certificateTemplateDetails.status = STATUS.FAILED;
        state.certificateTemplateDetails.error = action.payload?.message;
      });

    /* ---------- Get Certificate Template by Type ---------- */
    builder
      .addCase(getCertificateTemplatesByType.pending, (state) => {
        state.certificateTemplateByType.status = STATUS.LOADING;
        state.certificateTemplateByType.error = null;
      })
      .addCase(getCertificateTemplatesByType.fulfilled, (state, action) => {
        state.certificateTemplateByType.status = STATUS.SUCCEEDED;
        state.certificateTemplateByType.data = action?.payload || [];
      })
      .addCase(getCertificateTemplatesByType.rejected, (state, action) => {
        state.certificateTemplateByType.status = STATUS.FAILED;
        state.certificateTemplateByType.error = action.payload?.message;
      });

    /* ---------- Get Certificate Template by Type and Entity ---------- */
    builder
      .addCase(getCertificateTemplatesByTypeAndEntity.pending, (state) => {
        state.certificateTemplateByTypeAndEntity.status = STATUS.LOADING;
        state.certificateTemplateByTypeAndEntity.error = null;
      })
      .addCase(getCertificateTemplatesByTypeAndEntity.fulfilled, (state, action) => {
        state.certificateTemplateByTypeAndEntity.status = STATUS.SUCCEEDED;
        state.certificateTemplateByTypeAndEntity.data = action?.payload || [];
      })
      .addCase(getCertificateTemplatesByTypeAndEntity.rejected, (state, action) => {
        state.certificateTemplateByTypeAndEntity.status = STATUS.FAILED;
        state.certificateTemplateByTypeAndEntity.error = action.payload?.message;
      });
  },
});

/* -------------------- Selectors -------------------- */

export const selectCreateCertificateTemplate = (state) =>
  state.certificateTemplate.createCertificateTemplate;

export const selectCertificateTemplateList = (state) =>
  state.certificateTemplate.certificateTemplateList;

export const selectUpdateCertificateTemplate = (state) =>
  state.certificateTemplate.updateCertificateTemplate;

export const selectActiveCertificateTemplatesList = (state) =>
  state.certificateTemplate.activeCertificateTemplatesList;

export const getCertificateTemplate = (state) =>
  state.certificateTemplate.certificateTemplateDetails;

export const getCertificateTemplatesByCertificateType = (state) =>
  state.certificateTemplate.certificateTemplateByType;

export const selectCertificateTemplatesByTypeAndEntity = (state) =>
  state.certificateTemplate.certificateTemplateByTypeAndEntity;

export const { resetCertificateTemplateDetails } = certificateTemplateSlice.actions;

export default certificateTemplateSlice.reducer;
